import { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import qs from 'query-string';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Formik, FormikProps } from 'formik';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { ReactComponent as CubeAccrossIcon } from 'assets/icons/icon-cube-across.svg';
import { ReactComponent as CubeDown } from 'assets/icons/icon-cube-down.svg';
import { flattenTreeData, renderOptionsKtree, getListBoxPropsAutoScrolItem } from 'utils/Object';
import { useGetCubeDefinitionDetails } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { DIMENSION_CATEGORY, DropdownItemWithType } from 'types/api/Tenant/AROCube/CubeDataDimensionalViewTypes';
import { useGetCubeDimensionalViewFilterMember } from 'services/v1/Tenant/AROCube/CubeDimensionalViewService';
import { useGetCubeMemberDropdownByCube } from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import { InsightViewDimensionalOptionValue } from 'types/api/Tenant/AROCube/CubeInisghtViewTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { ControllerNavigation, ControllerNavigationDirection } from 'pages/Tenant/AroDashboard/ControllerNavigation';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { INSIGHT_VIEW_MEASUREMENT_TYPE, InsightViewType } from 'constant/ViewConstant';
import { useGetMyPersonalization, useGetMyViewDetails } from 'services/v1/Tenant/ConfigurationService';
import { PAGE_REF_NAVIGATION } from 'utils/Animation';
import {
  WorkFlowDrillNavigationDirection,
  getCurrentMyViewIndex,
  getNextDrillDownURL,
  getNextMyViewItem,
  getPreviousMyViewItem,
  getWorkFlowDrillView,
} from 'utils/MyViewUtils';
import { getQuickAccessNavigateURL } from 'components/LayoutComponent/VerticalLayout/QuickAccess';
import { useGetInsightViewDefinitionDetails } from 'services/v1/SystemTenant/AROView/InsightViewDefinitionService';
import { useGetInsightViewCubeAttributesDropdown } from 'services/v1/Tenant/AROCube/CubeInsightViewService';
import { useGetChartViewDetails } from 'services/v1/SystemTenant/AROChart/ChartViewDefinitionService';
import { ChartViewType } from 'constant/ChartViewConstant';
import { Checkbox, Chip } from '@mui/material';
import { KTreeNode } from 'types/api/Tenant/AROKMS/KTreeDataTypes';
import { filterOptionsBySelectedLevel } from 'pages/Tenant/AROChart/components/ChartViewPanel';
import { useGetChartViewPanelOptions } from 'services/v1/Tenant/AROChart/ChartViewService';
import { RowenaContent } from 'pages/Tenant/AroDashboard/RowenaContent';
import { CUBE_TIME_PERIOD_TYPE } from 'constant/CubeConstant';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

// Static root node for the cube member dropdown. Selected by default; when it is
// the selection no hierarchy filter is sent to the insight-view data API.
const HIERARCHY_FILTER_VALUE = 'HIERARCHY';
const HIERARCHY_FILTER_NODE = { value: HIERARCHY_FILTER_VALUE, label: 'Hierarchy', level: 0 };

export interface OptionItem {
  value: string | number;
  label: string;
}

const autoCompleteStyle = {
  width: 230,
  '& .MuiAutocomplete-popper': {
    backgroundColor: 'red !imoprtant',
    fontSize: '10px',
  },
};

const textInputStyle = {};

interface CubeInsightViewPanelProps {
  disabled?: boolean;
  insightViewId?: string;
  insightName?: string;
  cubeDefinitionId?: string;
  xDimensionOptions: DropdownItemWithType[];
  yDimensionOptions: DropdownItemWithType[];
  ref?: any;
  onOptionChange?: (option: InsightViewDimensionalOptionValue) => void;
  loading?: boolean;
  initialValues: InsightViewDimensionalOptionValue;
  timePeriodOptions: AutoCompleteItem[];
  currentTimePeriod: AutoCompleteItem | null;
  timePeriodType: string;
  activeCubeAttributeId?: string;
  totalDisplayedPeriods?: number;
  setIsLoadingOptionsData?: (isLoading: boolean) => void;
}

const formikInitialValues: InsightViewDimensionalOptionValue = {
  member: null,
  xDimensionType: null,
  yDimensionType: null,
  currentTimePeriod: null,
};

function getTimePeriodLabel(timePeriodType: string) {
  switch (timePeriodType) {
    case CUBE_TIME_PERIOD_TYPE.WEEK:
      return 'Selected Week';
    case CUBE_TIME_PERIOD_TYPE.MONTH:
      return 'Selected Month';
    case CUBE_TIME_PERIOD_TYPE.QUARTER:
      return 'Selected Quarter';
    case CUBE_TIME_PERIOD_TYPE.YEAR:
      return 'Selected Year';
  }
  return 'Selected Period';
}

export const CubeInsightViewPanel = forwardRef((props: CubeInsightViewPanelProps, ref) => {
  const {
    disabled: disableProps,
    initialValues,
    xDimensionOptions,
    onOptionChange,
    yDimensionOptions,
    cubeDefinitionId,
    insightViewId,
    setIsLoadingOptionsData,
    timePeriodOptions,
    currentTimePeriod,
    timePeriodType,
    activeCubeAttributeId,
    totalDisplayedPeriods,
  } = props;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedValues, setSelectedValues] = useState<KTreeNode[]>([]);
  const myViewId = searchParams.get('myViewId');
  const workFlowId = searchParams.get('workFlowId');
  const memberId = searchParams.get('memberId');
  const dashboardId = searchParams.get('fromDashboardId');
  const defaultFilterMember = searchParams.get('defaultFilterMember');
  const defaultYDimension = searchParams.get('defaultYDimension');
  const defaultXDimension = searchParams.get('defaultXDimension');
  const proxyMainMemberId = searchParams.get('proxyMainMemberId') || undefined;
  const chartMeasurmentId = searchParams.get('chartMeasurmentId');
  const refChartId = searchParams.get('refChartId') || undefined;
  const memberIds: string[] | undefined =
    searchParams.get('memberIds') !== null ? searchParams.getAll('memberIds') : undefined;

  const { data: chartViewDefintition } = useGetChartViewDetails(refChartId);
  const { data: chartViewPanel, isLoading: isFetchingPanelOptions } = useGetChartViewPanelOptions(refChartId);
  const { data: allMyViewsData } = useGetMyPersonalization({ enabled: true });
  const { data: myViewDetails } = useGetMyViewDetails(myViewId);

  const { data: insighViewDefinitionDetails } = useGetInsightViewDefinitionDetails(insightViewId);

  const insightViewList = useMemo(() => {
    if (allMyViewsData?.data) {
      const selectedDashboardId = myViewDetails?.data.dashboardId;
      const selectedDashboard =
        allMyViewsData.data.dashboards.find((dashboardItem) => dashboardItem.id === selectedDashboardId) || null;

      if (selectedDashboard) {
        return selectedDashboard.myViews;
      }
    }
    return [];
  }, [allMyViewsData?.data, myViewDetails?.data]);

  const [optionValue, setOptionValue] = useState<InsightViewDimensionalOptionValue>({
    xDimensionType: null,
    yDimensionType: null,
    member: null,
    showMemberOnly: false,
    measureChartCubeAttribute: undefined,
    members: null,
    currentTimePeriod: null,
    hierarchyFilterId: null,
  });

  const { data: cubeInsightAttributeDropdown, isLoading: isLoadingCubeAttribute } =
    useGetInsightViewCubeAttributesDropdown(
      insighViewDefinitionDetails?.data?.insightViewType === InsightViewType.CHART
        ? insighViewDefinitionDetails?.data?.cube?.value?.toString()
        : undefined,
      optionValue?.member?.value?.toString(),
    );

  const formikRef = useRef<FormikProps<InsightViewDimensionalOptionValue>>(null);
  const initialTimePeriodSetRef = useRef(false);

  const { data: memberDropdown, isLoading: isFetchingMember } = useGetCubeDimensionalViewFilterMember(
    'MAIN_MEMBER',
    cubeDefinitionId,
    optionValue.xDimensionType?.type,
    optionValue.yDimensionType?.type,
    DIMENSION_CATEGORY.CUBE_INSIGHT_VIEW,
    insightViewId,
    proxyMainMemberId,
  );

  const { data: cubeDefinitionDetails } = useGetCubeDefinitionDetails(cubeDefinitionId);

  const disabled = disableProps;

  const memberOptions = useMemo(() => {
    if (memberDropdown) {
      return flattenTreeData(memberDropdown);
    }
    return [];
  }, [memberDropdown]);

  const lowestMemberLevel = useMemo(() => {
    // @ts-ignore
    return memberOptions.reduce((acc, item) => {
      if (item.level > acc) {
        return item.level;
      } else {
        return acc;
      }
    }, 0);
  }, [memberOptions]);

  // Cube member dropdown copied from CubeDataEntryPanel — sourced from the same
  // `/api/v1/tenant/cube/dropdown/member/{cubeId}` endpoint and rendered after
  // the "Selected Period" dropdown below.
  const [selectedCubeMember, setSelectedCubeMember] = useState<AutoCompleteItem | null>(HIERARCHY_FILTER_NODE);
  const { data: cubeMemberDropdown, isLoading: isFetchingCubeMember } =
    useGetCubeMemberDropdownByCube(cubeDefinitionId);

  const cubeMemberOptions = useMemo(() => {
    if (cubeMemberDropdown) {
      return flattenTreeData(cubeMemberDropdown);
    }
    return [];
  }, [cubeMemberDropdown]);

  const lowestCubeMemberLevel = useMemo(() => {
    // @ts-ignore
    return cubeMemberOptions.reduce((acc, item) => {
      if (item.level > acc) {
        return item.level;
      } else {
        return acc;
      }
    }, 0);
  }, [cubeMemberOptions]);

  // Exclude leaf nodes — only show members that have children.
  const cubeMemberFilteredOptions = useMemo(() => {
    const parentValues = new Set<string | number>();
    const collectParents = (nodes?: KTreeNode[]) => {
      nodes?.forEach((node) => {
        if (node.children && node.children.length) {
          parentValues.add(node.value);
          collectParents(node.children);
        }
      });
    };
    collectParents(cubeMemberDropdown ?? undefined);
    // @ts-ignore
    const nonLeafOptions = cubeMemberOptions.filter((item) => parentValues.has(item.value));
    // Prepend the static "Hierarchy" root node as the default selectable option.
    return [HIERARCHY_FILTER_NODE, ...nonLeafOptions];
  }, [cubeMemberDropdown, cubeMemberOptions]);

  const currentMyViewItem = useMemo(() => {
    if (myViewDetails?.data) {
      return myViewDetails.data;
    }
    return null;
  }, [myViewDetails?.data]);

  const currentMyViewIndex = useMemo(() => {
    if (myViewId == null) return 0;
    return getCurrentMyViewIndex(insightViewList, myViewId);
  }, [myViewId, insightViewList]);

  const nextViewItem = useMemo(() => {
    return getNextMyViewItem(insightViewList, currentMyViewIndex);
  }, [currentMyViewIndex, insightViewList]);

  const previousViewItem = useMemo(() => {
    return getPreviousMyViewItem(insightViewList, currentMyViewIndex);
  }, [currentMyViewIndex, insightViewList]);

  const nextDrilDownWorkFlow = useMemo(() => {
    return getWorkFlowDrillView(currentMyViewItem, workFlowId, WorkFlowDrillNavigationDirection.DOWN);
  }, [currentMyViewItem, workFlowId]);

  const previousDrilDownWorkFlow = useMemo(() => {
    return getWorkFlowDrillView(currentMyViewItem, workFlowId, WorkFlowDrillNavigationDirection.UP);
  }, [currentMyViewItem, workFlowId]);

  const isMainMemberActiveAsFilter =
    (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER &&
      optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER) ||
    (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER &&
      optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER);

  const timePeriodLabel = getTimePeriodLabel(timePeriodType);

  const handleOnDashboardNavigate = (direction: ControllerNavigationDirection) => {
    if (!cubeDefinitionId) return;
    switch (direction) {
      case ControllerNavigationDirection.UP:
        const previousDrillDownURL = getNextDrillDownURL(currentMyViewItem, previousDrilDownWorkFlow, {
          memberId: optionValue.member?.value,
          chartMeasurmentId: optionValue.measureChartCubeAttribute?.value,
          ref: PAGE_REF_NAVIGATION.TOP,
          memberIds: selectedValues.map((item) => item.value),
        });
        if (previousDrillDownURL) {
          previousDrillDownURL && navigate(previousDrillDownURL);
        } else {
          const dashboardURL = `${PATH_CONSTANT.TENANT.DASHBOARD.DASHBOARD.replace(
            ':dashboardId',
            dashboardId || '',
          )}?${qs.stringify({
            memberId: optionValue.member?.value,
            ref: PAGE_REF_NAVIGATION.TOP,
          })}`;
          navigate(dashboardURL);
        }

        break;
      case ControllerNavigationDirection.DOWN:
        const nextDrillDownURL = getNextDrillDownURL(currentMyViewItem, nextDrilDownWorkFlow, {
          memberId: optionValue.member?.value,
          ref: PAGE_REF_NAVIGATION.BOTTOM,
          memberIds: selectedValues.map((item) => item.value),
          refChartId: refChartId,
          chartMeasurmentId: optionValue.measureChartCubeAttribute?.value,
        });
        nextDrillDownURL && navigate(nextDrillDownURL);
        break;

      case ControllerNavigationDirection.LEFT:
        const previousMyViewItemURL = getQuickAccessNavigateURL(previousViewItem, undefined, PAGE_REF_NAVIGATION.LEFT);
        previousMyViewItemURL && navigate(previousMyViewItemURL);
        break;

      case ControllerNavigationDirection.RIGHT:
        const nextMyViewItemURL = getQuickAccessNavigateURL(nextViewItem, undefined, PAGE_REF_NAVIGATION.RIGHT);
        nextMyViewItemURL && navigate(nextMyViewItemURL);

        break;

      default:
        break;
    }
  };

  const debouncedFunction = useMemo(
    () =>
      debounce((filteredValue: KTreeNode[]) => {
        setOptionValue((prev) => ({ ...prev, members: filteredValue }));
      }, 1000),
    [setOptionValue],
  );

  const debouncedSetOptionValue = useCallback(
    (filteredValue: KTreeNode[]) => {
      debouncedFunction(filteredValue);
    },
    [debouncedFunction],
  );
  const handleMultipleMemberChange = (event: React.SyntheticEvent, newValue: KTreeNode[]) => {
    setSelectedValues(newValue);
    debouncedSetOptionValue(newValue);
  };

  const memberMultipleSelectionOptions: KTreeNode[] = useMemo(() => {
    return filterOptionsBySelectedLevel(memberOptions, selectedValues);
  }, [memberOptions, selectedValues]);

  const navigationControllerTooltips = useMemo(() => {
    const defaultToolTips: {
      [key: string]: string;
    } = {
      [ControllerNavigationDirection.UP]: previousDrilDownWorkFlow?.componentName
        ? `Show ${previousDrilDownWorkFlow.componentName}`
        : 'Show Chart',
      [ControllerNavigationDirection.DOWN]: `Show ${nextDrilDownWorkFlow?.componentName}`,
    };

    if (insightViewList.length > 1) {
      if (currentMyViewIndex > -1) {
        if (currentMyViewIndex > 0) {
          defaultToolTips[ControllerNavigationDirection.LEFT] = `Show ${insightViewList[currentMyViewIndex - 1].title}`;
        }
        if (currentMyViewIndex < insightViewList.length - 1) {
          defaultToolTips[ControllerNavigationDirection.RIGHT] = `Show ${
            insightViewList[currentMyViewIndex + 1].title
          }`;
        }
      }
    }

    return defaultToolTips;
  }, [nextDrilDownWorkFlow, previousDrilDownWorkFlow?.componentName, insightViewList, currentMyViewIndex]);

  const mainMemberFilterLabel = useCallback(() => {
    if (cubeDefinitionDetails?.data) {
      if (
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MAIN_MEMBER) ||
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MAIN_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER)
      ) {
        return 'Period';
      }
      if (
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER) ||
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER)
      ) {
        return cubeDefinitionDetails?.data.member.label;
      }

      if (
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MAIN_MEMBER) ||
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MAIN_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER)
      ) {
        return 'Measure';
      }
      if (
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.SECOND_MEMBER) ||
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.SECOND_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER)
      ) {
        return cubeDefinitionDetails?.data.member.label;
      }

      if (
        (optionValue?.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.SECOND_MEMBER) ||
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.SECOND_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MEASUREMENT_MEMBER)
      ) {
        return cubeDefinitionDetails?.data.member.label;
      }

      if (
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.MAIN_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.SECOND_MEMBER) ||
        (optionValue.xDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER &&
          optionValue.yDimensionType?.type === INSIGHT_VIEW_MEASUREMENT_TYPE.INSIGHT_COLUMNS_MEMBER)
      ) {
        return 'Measure';
      }
    }
    return 'Member';
  }, [optionValue.xDimensionType?.type, optionValue.yDimensionType?.type, cubeDefinitionDetails?.data]);

  useEffect(() => {
    if (memberIds && memberIds.length > 0 && memberOptions) {
      const selectedMembers = memberOptions.filter((item: KTreeNode) => memberIds.includes(item.value));
      setSelectedValues(selectedMembers);
      setOptionValue((prev) => ({ ...prev, members: selectedMembers }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOptions]);

  useEffect(() => {
    if (
      chartViewDefintition?.data?.type === ChartViewType.MULTI_MEMBER_SELECTION &&
      chartViewPanel?.data?.accumulationOptions
    ) {
      setOptionValue((prev) => ({ ...prev, refChartAccumulationType: chartViewPanel?.data?.accumulationOptions[0] }));
    }
  }, [chartViewDefintition?.data?.type, chartViewPanel?.data?.accumulationOptions]);

  useEffect(() => {
    onOptionChange && onOptionChange(optionValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionValue]);

  useEffect(() => {
    if (initialValues) {
      setOptionValue((prevState) => ({
        ...prevState,
        xDimensionType: initialValues.xDimensionType,
        yDimensionType: initialValues.yDimensionType,
      }));
    }
  }, [initialValues]);

  // Reset initial time period set flag when insightViewId changes
  useEffect(() => {
    initialTimePeriodSetRef.current = false;
  }, [insightViewId]);

  // auto select measure attribute
  useEffect(() => {
    if (cubeInsightAttributeDropdown?.data && cubeInsightAttributeDropdown?.data.length > 0) {
      const measure =
        cubeInsightAttributeDropdown?.data.find((item) => item.value === chartMeasurmentId) ||
        cubeInsightAttributeDropdown?.data[0];
      const selectedMeasure: AutoCompleteItem = {
        value: measure?.value?.toString() || '',
        label: measure.label,
      };
      setOptionValue((prev) => ({ ...prev, measureChartCubeAttribute: selectedMeasure }));
    }
  }, [cubeInsightAttributeDropdown?.data, chartMeasurmentId]);

  // auto select member
  useEffect(() => {
    if (memberOptions && memberOptions?.length > 0) {
      const member = memberOptions?.find((item: DropdownItem) => item.value === memberId) ?? memberOptions?.[0];
      const selectedMember: DropdownItem = {
        id: member.value,
        value: member?.value?.toString() || '',
        label: member.label,
      };
      setOptionValue((prev) => ({ ...prev, member: selectedMember }));
    }
  }, [memberOptions, memberId]);

  // auto select dimension
  useEffect(() => {
    if (defaultXDimension) {
      const xDimension = xDimensionOptions.find((item) => item.type === defaultXDimension);
      if (xDimension) {
        setOptionValue((prev) => ({ ...prev, xDimensionType: xDimension }));
      }
    }
    if (defaultYDimension) {
      const yDimension = yDimensionOptions.find((item) => item.type === defaultYDimension);

      if (yDimension) {
        setOptionValue((prev) => ({ ...prev, yDimensionType: yDimension }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xDimensionOptions, yDimensionOptions]);

  // auto select time period
  useEffect(() => {
    if (currentTimePeriod && !initialTimePeriodSetRef.current) {
      const selectedTimePeriod: AutoCompleteItem = {
        value: currentTimePeriod?.value?.toString() || '',
        label: currentTimePeriod?.label || '',
      };
      setOptionValue((prev) => ({ ...prev, currentTimePeriod: selectedTimePeriod }));
      initialTimePeriodSetRef.current = true;
    }
  }, [currentTimePeriod]);

  useEffect(() => {
    if (memberOptions.length > 0 && defaultFilterMember && !isMainMemberActiveAsFilter) {
      const member =
        memberOptions.find((item: DropdownItem) => item.value === defaultFilterMember) ?? memberOptions?.[0];

      if (member) {
        setOptionValue((prev) => ({ ...prev, member: member }));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOptions]);

  const [optionFilterWidths, setOptionFilterWidths] = useState({
    downFilter: 'auto',
    acrossFilter: 'auto',
    timePeriodFilter: 'auto',
  });

  useEffect(() => {
    const label = optionValue.xDimensionType?.label ?? '';
    setOptionFilterWidths((prev) => ({
      ...prev,
      downFilter: `${label.length}ch`,
    }));
  }, [optionValue.xDimensionType]);

  useEffect(() => {
    const label = optionValue.yDimensionType?.label ?? '';
    setOptionFilterWidths((prev) => ({
      ...prev,
      acrossFilter: `${label.length}ch`,
    }));
  }, [optionValue.yDimensionType]);

  useEffect(() => {
    const label = optionValue.currentTimePeriod?.label ?? '';
    setOptionFilterWidths((prev) => ({
      ...prev,
      timePeriodFilter: `${label.length}ch`,
    }));
  }, [optionValue.currentTimePeriod]);

  useImperativeHandle(ref, () => ({
    isLoadingOptionsData: isFetchingMember,
  }));

  const resetSearchParamsFromDefaultFilter = () => {
    searchParams.delete('defaultFilterMember');
    searchParams.delete('defaultYDimension');
    searchParams.delete('defaultXDimension');
    setSearchParams(searchParams);
  };

  useEffect(() => {
    setIsLoadingOptionsData && setIsLoadingOptionsData(isFetchingMember);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingMember]);

  const controllerNaviations = useMemo(() => {
    const controller = [];
    if (nextDrilDownWorkFlow && previousDrilDownWorkFlow) {
      controller.push(ControllerNavigationDirection.UP);
      controller.push(ControllerNavigationDirection.DOWN);
    }
    if (previousDrilDownWorkFlow && !nextDrilDownWorkFlow) {
      controller.push(ControllerNavigationDirection.UP);
    }
    if (!previousDrilDownWorkFlow && nextDrilDownWorkFlow) {
      controller.push(ControllerNavigationDirection.DOWN);
    }

    if (insightViewList?.length > 1) {
      if (currentMyViewIndex > 0) controller.push(ControllerNavigationDirection.LEFT);
      if (currentMyViewIndex < insightViewList.length - 1) controller.push(ControllerNavigationDirection.RIGHT);
    }

    return controller;
  }, [insightViewList, currentMyViewIndex, nextDrilDownWorkFlow, previousDrilDownWorkFlow]);

  return (
    <Stack direction='column' sx={{ backgroundColor: '#fff', py: 1 }}>
      <Formik onSubmit={() => {}} innerRef={formikRef} initialValues={formikInitialValues}>
        {({ errors, touched, handleBlur, handleChange, values, setFieldValue, handleSubmit }) => {
          return (
            <>
              <Stack
                direction='row'
                alignItems='center'
                sx={{ bgcolor: '#FBFBFB', py: 1, px: 2, border: '1px solid #E3EBF6' }}
              >
                <Stack direction='row' justifyContent='space-between' width='100%'>
                  <Stack direction='row' spacing={3} alignItems='center'>
                    {(!refChartId || chartViewDefintition?.data?.type === ChartViewType.SINGLE_MEMBER_SELECTION) && (
                      <Autocomplete
                        // @ts-ignore
                        onChange={(event, value) => {
                          setOptionValue((prev) => ({ ...prev, member: value }));
                        }}
                        clearIcon={null}
                        sx={{
                          width: 300,
                        }}
                        size='small'
                        value={optionValue.member}
                        disabled={disabled || isFetchingMember}
                        getOptionLabel={(option: AutoCompleteItem) => option.label}
                        options={memberOptions || []}
                        renderOption={(props, option, state) =>
                          renderOptionsKtree(props, option, state, lowestMemberLevel)
                        }
                        ListboxProps={{
                          ...getListBoxPropsAutoScrolItem(memberOptions, optionValue.member),
                        }}
                        renderInput={(params) => (
                          <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                            <TextField
                              {...params}
                              sx={textInputStyle}
                              label={isFetchingMember ? 'Loading...' : mainMemberFilterLabel() || 'Member'}
                              onBlur={handleBlur}
                              name='member'
                            />
                          </Stack>
                        )}
                      />
                    )}

                    {chartViewDefintition?.data.type === ChartViewType.MULTI_MEMBER_SELECTION && (
                      <Autocomplete
                        multiple
                        onChange={handleMultipleMemberChange}
                        sx={{
                          width: 350,
                        }}
                        size='small'
                        value={selectedValues}
                        isOptionEqualToValue={(option: KTreeNode, value: KTreeNode) => option.value === value.value}
                        getOptionDisabled={(option) => {
                          // If we have a selected level (say `selectedLevel`), only enable
                          // items from that level. If no selection, no disabling.
                          const selectedLevelSet = new Set(selectedValues.map((item) => item.level));
                          if (selectedLevelSet.size === 0) return false; // no selection yet => enable all
                          if (selectedLevelSet.size === 1) {
                            // @ts-ignore
                            const [selectedLevel] = [...selectedLevelSet];
                            return option.level !== selectedLevel;
                          }
                          return false;
                        }}
                        disabled={isFetchingPanelOptions}
                        getOptionLabel={(option: KTreeNode) => option.label}
                        disableCloseOnSelect
                        renderTags={(value, getTagProps) => {
                          if (value.length === 0) return null;
                          return (
                            <Chip
                              label={`${value.length} item${value.length > 1 ? 's' : ''} selected`}
                              color='primary'
                            />
                          );
                        }}
                        options={memberMultipleSelectionOptions || []}
                        renderOption={(
                          props: React.HTMLAttributes<HTMLLIElement>,
                          option: AutoCompleteItem,
                          { selected },
                        ) => {
                          // @ts-ignore
                          const isLowestLevel = option.level === lowestMemberLevel;
                          const styleObj: React.CSSProperties = isLowestLevel
                            ? {
                                // @ts-ignore
                                marginLeft: `${option.level * 3}px`,
                                fontSize: '14px',
                              }
                            : {
                                fontWeight: 'bold',
                                // @ts-ignore
                                marginLeft: `${option.level * 2}px`,
                                borderBottom: '1px solid #e3e3e3',
                                fontSize: '14px',
                              };

                          return (
                            <li {...props} key={option.value} style={styleObj}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                                sx={{
                                  color: '#828C99',
                                  px: 0,
                                  paddingRight: 0.5,
                                }}
                              />
                              {option.label}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                            <TextField
                              {...params}
                              sx={textInputStyle}
                              label={isFetchingMember ? 'Loading...' : mainMemberFilterLabel() || 'Member'}
                              onBlur={handleBlur}
                              name='member'
                              value={`Selected va`}
                            />
                          </Stack>
                        )}
                      />
                    )}
                    {insighViewDefinitionDetails?.data?.insightViewType === InsightViewType.CHART && (
                      <Autocomplete
                        // @ts-ignore
                        onChange={(event, value) => {
                          setOptionValue((prev) => ({
                            ...prev,
                            measureChartCubeAttribute: value,
                          }));
                        }}
                        clearIcon={null}
                        size='small'
                        value={{
                          label: optionValue.measureChartCubeAttribute?.label || 'Measurement',
                          value: optionValue.measureChartCubeAttribute?.value || '',
                        }}
                        disabled={disabled || isLoadingCubeAttribute}
                        getOptionLabel={(option: AutoCompleteItem) => option.label}
                        options={cubeInsightAttributeDropdown?.data || []}
                        renderOption={(props, option) => (
                          <Box
                            component='li'
                            sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }}
                            {...props}
                          >
                            <span>{option.label}</span>
                          </Box>
                        )}
                        sx={autoCompleteStyle}
                        renderInput={(params) => (
                          <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                            <TextField
                              {...params}
                              sx={textInputStyle}
                              label={isFetchingMember ? 'Loading...' : 'Measurement'}
                              onBlur={handleBlur}
                              placeholder={isFetchingMember ? 'Loading...' : 'Select Measurement'}
                              name='measureChartCubeAttribute'
                            />
                          </Stack>
                        )}
                      />
                    )}

                    {insighViewDefinitionDetails?.data?.insightViewType === InsightViewType.REPORT && (
                      <>
                        <Autocomplete
                          // @ts-ignore

                          onChange={(event, value) => {
                            if (!value) return;
                            resetSearchParamsFromDefaultFilter();
                            setOptionValue((prev) => ({
                              ...prev,
                              xDimensionType: value,
                            }));
                          }}
                          clearIcon={null}
                          size='small'
                          value={optionValue?.xDimensionType}
                          disabled={disabled}
                          getOptionLabel={(option: DropdownItem) => option.label}
                          options={xDimensionOptions || []}
                          renderOption={(props, option) => (
                            <Box
                              component='li'
                              sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }}
                              {...props}
                            >
                              <span>{option.label}</span>
                            </Box>
                          )}
                          sx={{ ...autoCompleteStyle, width: 'auto', minWidth: 120 }}
                          renderInput={(params) => (
                            <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                              <TextField
                                {...params}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    width: optionFilterWidths.downFilter,
                                    transition: 'width 200ms ease-in-out',
                                  },
                                }}
                                sx={textInputStyle}
                                onBlur={handleBlur}
                                label={'Down'}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position='start'>
                                      <CubeDown />
                                    </InputAdornment>
                                  ),
                                }}
                                name='xDimensionType'
                              />
                            </Stack>
                          )}
                        />
                        <Autocomplete
                          // @ts-ignore
                          onChange={(event, value) => {
                            resetSearchParamsFromDefaultFilter();
                            setOptionValue((prev) => ({
                              ...prev,
                              yDimensionType: value,
                            }));
                          }}
                          clearIcon={null}
                          size='small'
                          value={optionValue.yDimensionType}
                          disabled={disabled}
                          getOptionLabel={(option: DropdownItem) => option.label}
                          options={yDimensionOptions || []}
                          renderOption={(props, option) => (
                            <Box
                              component='li'
                              sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }}
                              {...props}
                            >
                              <span>{option.label}</span>
                            </Box>
                          )}
                          sx={{ ...autoCompleteStyle, width: 'auto', minWidth: 120 }}
                          renderInput={(params) => (
                            <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                              <TextField
                                {...params}
                                sx={textInputStyle}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    width: optionFilterWidths.acrossFilter,
                                    transition: 'width 200ms ease-in-out',
                                  },
                                }}
                                label='Across'
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position='start'>
                                      <CubeAccrossIcon />
                                    </InputAdornment>
                                  ),
                                }}
                                onBlur={handleBlur}
                                name='yDimenstionType'
                              />
                            </Stack>
                          )}
                        />
                        <Autocomplete
                          // @ts-ignore
                          onChange={(event, value) => {
                            resetSearchParamsFromDefaultFilter();
                            setOptionValue((prev) => ({
                              ...prev,
                              currentTimePeriod: value,
                            }));
                          }}
                          clearIcon={null}
                          size='small'
                          isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) =>
                            option.value === value.value
                          }
                          value={optionValue.currentTimePeriod}
                          disabled={disabled}
                          getOptionLabel={(option: AutoCompleteItem) => option.label}
                          options={timePeriodOptions || []}
                          renderOption={(props, option) => (
                            <Box
                              component='li'
                              sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }}
                              {...props}
                            >
                              <span>{option.label}</span>
                            </Box>
                          )}
                          sx={{ ...autoCompleteStyle, width: 'auto', minWidth: 120 }}
                          ListboxProps={{
                            ...getListBoxPropsAutoScrolItem(timePeriodOptions, optionValue.currentTimePeriod),
                          }}
                          renderInput={(params) => (
                            <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                              <TextField
                                {...params}
                                sx={textInputStyle}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    width: optionFilterWidths.timePeriodFilter,
                                    transition: 'width 200ms ease-in-out',
                                  },
                                }}
                                label={timePeriodLabel}
                                onBlur={handleBlur}
                                name='currentTimePeriod'
                              />
                            </Stack>
                          )}
                        />
                        {(optionValue.xDimensionType?.type === 'MAIN_MEMBER' ||
                          optionValue.yDimensionType?.type === 'MAIN_MEMBER') && (
                          <Autocomplete
                            // @ts-ignore
                            onChange={(event, value) => {
                              if (!!value) {
                                setSelectedCubeMember(value);
                                setOptionValue((prev) => ({
                                  ...prev,
                                  hierarchyFilterId:
                                    value.value === HIERARCHY_FILTER_VALUE ? null : String(value.value),
                                }));
                              }
                            }}
                            clearIcon={null}
                            sx={{
                              width: 300,
                            }}
                            size='small'
                            value={selectedCubeMember}
                            isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) =>
                              option.value === value.value
                            }
                            disabled={disabled || isFetchingCubeMember}
                            getOptionLabel={(option: AutoCompleteItem) => option.label}
                            options={cubeMemberFilteredOptions || []}
                            renderOption={(props, option, state) =>
                              renderOptionsKtree(props, option, state, lowestCubeMemberLevel)
                            }
                            ListboxProps={{
                              ...getListBoxPropsAutoScrolItem(cubeMemberFilteredOptions, selectedCubeMember),
                            }}
                            renderInput={(params) => (
                              <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                                <TextField
                                  {...params}
                                  sx={textInputStyle}
                                  label={isFetchingCubeMember ? 'Loading...' : 'Context'}
                                  onBlur={handleBlur}
                                  name='cubeMember'
                                />
                              </Stack>
                            )}
                          />
                        )}
                      </>
                    )}

                    {chartViewDefintition?.data?.type === ChartViewType.MULTI_MEMBER_SELECTION && (
                      <Autocomplete
                        onChange={(event, value) => {
                          setOptionValue((prev) => ({
                            ...prev,
                            refChartAccumulationType: value,
                          }));
                        }}
                        clearIcon={null}
                        size='small'
                        isOptionEqualToValue={(option: AutoCompleteItem, value: AutoCompleteItem) =>
                          option.value === value.value
                        }
                        value={{
                          label: optionValue.refChartAccumulationType?.label || 'Accumulation',
                          value: optionValue.refChartAccumulationType?.value || '',
                        }}
                        disabled={disabled || isFetchingMember}
                        getOptionLabel={(option: AutoCompleteItem) => option.label}
                        options={chartViewPanel?.data.accumulationOptions || []}
                        renderOption={(props, option) => (
                          <Box
                            component='li'
                            sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }}
                            {...props}
                          >
                            <span>{option.label}</span>
                          </Box>
                        )}
                        sx={autoCompleteStyle}
                        renderInput={(params) => (
                          <Stack direction='row' justifyContent='space-between' display='flex' alignItems='center'>
                            <TextField
                              {...params}
                              sx={textInputStyle}
                              label={isFetchingMember ? 'Loading...' : 'Accumulation'}
                              onBlur={handleBlur}
                              placeholder={isFetchingMember ? 'Loading...' : 'Select Accumulation'}
                              name='accumulation'
                            />
                          </Stack>
                        )}
                      />
                    )}
                  </Stack>
                </Stack>

                <Stack mr={2}>
                  {(() => {
                    const cubeAttributeId =
                      activeCubeAttributeId ??
                      (optionValue.measureChartCubeAttribute?.value
                        ? optionValue.measureChartCubeAttribute.value.toString()
                        : undefined);
                    return (
                      <RowenaContent
                        context={{
                          measure: cubeAttributeId ? { cubeAttributeId } : undefined,
                          timePeriod: optionValue.currentTimePeriod?.label
                            ? { value: optionValue.currentTimePeriod.label }
                            : undefined,
                          totalPeriod:
                            typeof totalDisplayedPeriods === 'number'
                              ? { value: totalDisplayedPeriods }
                              : undefined,
                        }}
                      />
                    );
                  })()}
                </Stack>
                <ControllerNavigation
                  enableNavigations={controllerNaviations}
                  onNavigate={handleOnDashboardNavigate}
                  tooltips={navigationControllerTooltips}
                />
              </Stack>
              {/* <Stack direction='row' alignItems='center' sx={{ pt: 1, px: 2 }} width='100%'>
                <Stack direction='row' gap={3} width='100%' alignItems='center'></Stack>
              </Stack> */}
            </>
          );
        }}
      </Formik>
    </Stack>
  );
});
